import { useCallback, useEffect, useRef, useState } from 'react';
import { SubmissionsMeta } from '../../StandaloneCourses/Submissions/Submissions.types';

export type PagedResponse<T> = { data: T[]; meta: SubmissionsMeta };

/**
 * One paginated submissions list, for the global tab and the in-product
 * queues alike:
 * - a monotonic request id, so only the latest response lands whatever
 *   order the network returns them;
 * - a filter change goes back to page 1 in ONE request (the old pair of
 *   effects fired load(N) and then load(1), and the backend ran both);
 * - `reload` for after a grading modal closes.
 *
 * `deps` must be JSON-serialisable (the filter values); `fetcher` may be an
 * inline closure — it is read through a ref, so its identity never re-runs
 * the effect.
 */
export function usePagedSubmissions<T>(
	fetcher: (page: number) => Promise<PagedResponse<T>>,
	deps: unknown[],
	onError?: (error: unknown) => void
) {
	const [page, setPage] = useState(1);
	const [rows, setRows] = useState<T[]>([]);
	const [meta, setMeta] = useState<SubmissionsMeta>({});
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;
	const onErrorRef = useRef(onError);
	onErrorRef.current = onError;
	const requestId = useRef(0);

	const load = useCallback(async (activePage: number) => {
		const id = ++requestId.current;
		try {
			const response = await fetcherRef.current(activePage);
			if (id !== requestId.current) return;
			setRows(response.data ?? []);
			setMeta(response.meta ?? {});
		} catch (error) {
			if (id !== requestId.current) return;
			onErrorRef.current?.(error);
		}
	}, []);

	const depsKey = JSON.stringify(deps);
	const lastKey = useRef<string | undefined>(undefined);
	useEffect(() => {
		const keyChanged = lastKey.current !== undefined && lastKey.current !== depsKey;
		lastKey.current = depsKey;
		// A filter change on page N: reset to 1 and let the re-run load it.
		if (keyChanged && page !== 1) {
			setPage(1);
			return;
		}
		load(page);
	}, [depsKey, page, load]);

	return { page, setPage, rows, meta, reload: () => load(page) };
}

export default usePagedSubmissions;
